import React, { useEffect, useState } from 'react';

const TableHelperPanel = ({ databaseId, hiddenTables = [] }) => {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [columns, setColumns] = useState([]);
  const [sampleRows, setSampleRows] = useState([]);
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTables = async () => {
      if (!databaseId) return;

      setIsLoadingTables(true);
      setError('');
      setTables([]);
      setSelectedTable('');
      setColumns([]);
      setSampleRows([]);

      try {
        const response = await fetch(`/api/database/${databaseId}/tables`);
        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.error || 'Gagal mengambil daftar tabel');
        }

        const allTables = Array.isArray(result) ? result : [];
        const tableList = allTables.filter(t => !hiddenTables.includes(t));
        setTables(tableList);

        if (tableList.length > 0) {
          setSelectedTable(tableList[0]);
        }
      } catch (err) {
        console.error('Error fetching tables:', err);
        setError(err.message);
      } finally {
        setIsLoadingTables(false);
      }
    };

    fetchTables();
  }, [databaseId, hiddenTables]);

  useEffect(() => {
    const fetchTableDetail = async () => {
      if (!databaseId || !selectedTable) return;

      setIsLoadingDetail(true);
      setError('');
      setColumns([]);
      setSampleRows([]);

      try {
        const [columnsResponse, rowsResponse] = await Promise.all([
          fetch(`/api/database/${databaseId}/table/${encodeURIComponent(selectedTable)}/columns`),
          fetch(`/api/database/${databaseId}/data/${encodeURIComponent(selectedTable)}?limit=3`)
        ]);

        const columnsResult = await columnsResponse.json();
        const rowsResult = await rowsResponse.json();

        if (!columnsResponse.ok) {
          throw new Error(columnsResult.error || 'Gagal mengambil struktur tabel');
        }

        if (!rowsResponse.ok) {
          throw new Error(rowsResult.error || 'Gagal mengambil contoh data');
        }

        setColumns(Array.isArray(columnsResult) ? columnsResult : []);
        setSampleRows(Array.isArray(rowsResult) ? rowsResult : []);
      } catch (err) {
        console.error('Error fetching table detail:', err);
        setError(err.message);
      } finally {
        setIsLoadingDetail(false);
      }
    };

    fetchTableDetail();
  }, [databaseId, selectedTable]);

  const sampleColumns = sampleRows.length > 0 ? Object.keys(sampleRows[0]) : [];

  const exampleQuery = selectedTable
    ? `SELECT *\nFROM \`${selectedTable}\`\nLIMIT 10;`
    : '';

  const handleCopyQuery = async () => {
    try {
      await navigator.clipboard.writeText(exampleQuery);
      alert('Contoh query berhasil disalin.');
    } catch (error) {
      console.error('Gagal menyalin query:', error);
    }
  };

  return (
    <div
      style={{
        border: '1px solid #ddd',
        borderRadius: '6px',
        padding: '15px',
        marginBottom: '20px',
        backgroundColor: '#fafafa',
        /* FIX: pastikan tidak keluar dari parent */
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      <h2 style={{ marginTop: 0 }}>Bantuan Struktur Tabel</h2>
      <p style={{ color: '#666', marginTop: 0 }}>
        Gunakan bagian ini untuk melihat nama tabel, nama kolom, tipe data, dan 3 baris pertama sebelum menulis query.
      </p>

      {!databaseId ? (
        <p style={{ color: '#666' }}>Pilih database terlebih dahulu.</p>
      ) : isLoadingTables ? (
        <p>Memuat daftar tabel...</p>
      ) : tables.length === 0 ? (
        <p style={{ color: '#666' }}>Tidak ada tabel pada database ini.</p>
      ) : (
        <>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>
              Pilih Tabel
            </label>
            <select
              value={selectedTable}
              onChange={(e) => setSelectedTable(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ddd',
                backgroundColor: 'white',
                boxSizing: 'border-box'
              }}
            >
              {tables.map(tableName => (
                <option key={tableName} value={tableName}>
                  {tableName}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <div className="input-error-message" style={{ marginBottom: '10px' }}>
              {error}
            </div>
          )}

          {isLoadingDetail ? (
            <p>Memuat struktur tabel dan contoh data...</p>
          ) : (
            <>
              <h3>Struktur Kolom</h3>

              {columns.length === 0 ? (
                <p style={{ color: '#666' }}>Struktur kolom belum tersedia.</p>
              ) : (
                /* FIX: wrapper dengan overflow-x auto, lebar dibatasi */
                <div
                  className="data-table-container"
                  style={{
                    marginBottom: '15px',
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'auto'
                  }}
                >
                  <table
                    className="data-table"
                    style={{
                      width: '100%',
                      tableLayout: 'fixed', /* FIX: kolom tidak melar bebas */
                      borderCollapse: 'collapse'
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={{ width: '50%' }}>Nama Kolom</th>
                        <th style={{ width: '50%' }}>Tipe Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {columns.map((col, index) => (
                        <tr key={`${col.nama_kolom || col.Field}-${index}`}>
                          <td
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={col.nama_kolom || col.Field}
                          >
                            {col.nama_kolom || col.Field}
                          </td>
                          <td
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={col.tipe_data || col.Type}
                          >
                            {col.tipe_data || col.Type}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <h3>Contoh 3 Data Pertama</h3>

              {sampleRows.length === 0 ? (
                <p style={{ color: '#666' }}>Contoh data tidak tersedia.</p>
              ) : (
                /* FIX: scroll horizontal untuk tabel data yang banyak kolom */
                <div
                  className="data-table-container"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    overflowX: 'auto'
                  }}
                >
                  <table
                    className="data-table"
                    style={{
                      /* FIX: hapus whiteSpace nowrap dari level table,
                         pindah ke td dengan maxWidth agar tidak melar tak terbatas */
                      minWidth: '400px',
                      borderCollapse: 'collapse'
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={{ width: '40px', minWidth: '40px' }}>#</th>
                        {sampleColumns.map(col => (
                          <th
                            key={col}
                            style={{
                              maxWidth: '160px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                            title={col}
                          >
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sampleRows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          <td style={{ width: '40px', minWidth: '40px' }}>{rowIndex + 1}</td>
                          {sampleColumns.map(col => (
                            <td
                              key={col}
                              style={{
                                maxWidth: '160px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={String(row[col] ?? '')}
                            >
                              {String(row[col] ?? '')}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedTable && (
                <div
                  style={{
                    marginTop: '15px',
                    padding: '12px',
                    backgroundColor: '#e3f2fd',
                    borderRadius: '4px',
                    /* FIX: jangan melar keluar container */
                    width: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                    <strong>Contoh Query</strong>
                    <button
                      type="button"
                      onClick={handleCopyQuery}
                      style={{
                        padding: '6px 10px',
                        border: 'none',
                        borderRadius: '4px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        cursor: 'pointer',
                        flexShrink: 0
                      }}
                    >
                      Salin
                    </button>
                  </div>

                  <pre
                    style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word', /* FIX: teks panjang wrap */
                      marginBottom: 0,
                      overflowX: 'auto'
                    }}
                  >
                    {exampleQuery}
                  </pre>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default TableHelperPanel;